import React from "react";
import { FileText, Sparkles, Upload, Target, Building2, BookOpen } from "lucide-react";
import FeaturePage, { MockWindow, ScoreBar } from "./FeaturePage";
import { siblingLinks } from "./links";

const ACCENT = "#0F172A";

// ── Hero visual: doc → generated questions ──
function HeroVisual() {
  return (
    <MockWindow label="CUSTOM INTERVIEW · FROM YOUR DOC" accent={ACCENT}>
      <div className="space-y-4">
        <div className="flex items-center gap-3 rounded-xl border border-border p-3.5 bg-secondary/40">
          <div className="w-8 h-8 rounded-lg bg-[#0F172A]/8 flex items-center justify-center flex-shrink-0">
            <FileText className="w-4 h-4 text-[#0F172A]" />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-sm text-[#0F172A] truncate">ICU_Registered_Nurse_JD.pdf</div>
            <div className="text-[11px] text-muted-foreground">Parsed · 6 questions generated</div>
          </div>
        </div>
        <div className="space-y-2">
          {[
            "How do you prioritise care when two patients deteriorate at once?",
            "Walk me through your response to a rapid deterioration.",
            "Describe a time you disagreed with a physician about a care plan.",
          ].map((q, i) => (
            <div key={i} className="flex gap-2.5 rounded-xl border border-border p-3">
              <span className="font-mono text-[10px] mt-0.5" style={{ color: ACCENT }}>Q{i + 1}</span>
              <p className="text-xs text-[#0F172A] leading-snug">{q}</p>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground">
          <Sparkles className="w-3 h-3" style={{ color: ACCENT }} /> Graded strictly against your exact material
        </div>
      </div>
    </MockWindow>
  );
}

// ── Row 1 visual: upload dropzone ──
function UploadVisual() {
  return (
    <MockWindow label="ADD YOUR MATERIAL" accent={ACCENT}>
      <div className="space-y-3">
        <div className="rounded-xl border-2 border-dashed border-border p-8 text-center">
          <div className="w-12 h-12 rounded-xl bg-[#0F172A]/8 flex items-center justify-center mx-auto mb-3">
            <Upload className="w-6 h-6 text-[#0F172A]" />
          </div>
          <p className="text-sm font-semibold text-[#0F172A]">Drop a JD or company doc</p>
          <p className="text-[11px] text-muted-foreground mt-1">PDF, DOCX, or paste text</p>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {["Job description", "Company wiki", "Product spec"].map((t) => (
            <div key={t} className="rounded-lg border border-border p-2 text-center text-[10px] font-mono text-muted-foreground">{t}</div>
          ))}
        </div>
      </div>
    </MockWindow>
  );
}

// ── Row 2 visual: strict grading against source ──
function GradingVisual() {
  return (
    <MockWindow label="GRADED AGAINST YOUR DOC" badge="Match 91" accent={ACCENT}>
      <div className="space-y-3">
        <ScoreBar label="ANSWER vs SOURCE MATERIAL" value={91} accent={ACCENT} suffix="/100" />
        <div className="rounded-xl border border-border p-3.5">
          <div className="font-mono text-[10px] tracking-[0.15em] mb-2" style={{ color: ACCENT }}>FOUND IN YOUR DOC</div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            "…escalate via the rapid-response protocol and hand off using SBAR…" — matched to your answer.
          </p>
        </div>
        <div className="rounded-xl border p-3.5" style={{ borderColor: "#F59E0B33", background: "#F59E0B0D" }}>
          <div className="font-mono text-[10px] tracking-[0.15em] mb-2 text-[#B45309]">MISSED</div>
          <p className="text-xs text-muted-foreground leading-relaxed">Didn't mention family communication or documentation timelines from section 3.</p>
        </div>
      </div>
    </MockWindow>
  );
}

export default function CustomInterviewFeature() {
  return (
    <FeaturePage
      accent={ACCENT}
      eyebrow="CUSTOM INTERVIEW"
      title="Bring your own docs."
      titleAccent="We build the interview."
      subtitle="Upload the actual job description, a company wiki, or any document — and the AI generates tailored questions, then grades you strictly against your exact material. It's the closest thing to walking in with the answer key."
      heroChips={["Any JD or document", "AI-generated questions", "Graded against your source"]}
      heroVisual={<HeroVisual />}
      rows={[
        {
          tag: "YOUR MATERIAL IN",
          title: "Start from the real thing, not a template.",
          body: "Paste or upload the exact spec for the role you're chasing. The AI reads it end-to-end and understands the skills, tools, and responsibilities that actually matter for this job.",
          bullets: [
            "Upload a PDF/DOCX or paste text",
            "Job descriptions, wikis, product specs",
            "No generic question banks — it's all yours",
          ],
          visual: <UploadVisual />,
        },
        {
          tag: "TAILORED QUESTIONS",
          title: "Questions you'll genuinely get asked.",
          body: "The AI turns your document into a focused set of interview questions — the same themes a real interviewer would pull from that spec. Practise the specifics, not the generic.",
          bullets: [
            "Questions mapped to your document",
            "Right depth for the seniority",
            "Refreshable — regenerate any time",
          ],
          visual: <HeroVisual />,
          reverse: true,
        },
        {
          tag: "STRICT GRADING",
          title: "Scored against your exact source.",
          body: "Answers are checked against the material you provided — not a vague rubric. You see what you nailed, what you missed, and the precise passages your answer should have covered.",
          bullets: [
            "Answer-vs-source match score",
            "Exactly what you missed and where",
            "Evidence quoted from your document",
          ],
          visual: <GradingVisual />,
        },
      ]}
      steps={[
        { title: "Add your document", body: "Upload a JD or company doc, or paste the text you want to be interviewed on." },
        { title: "Get tailored questions", body: "The AI generates a focused interview from your exact material." },
        { title: "Answer & get graded", body: "Respond, then see how well you covered the source — down to the passage." },
      ]}
      useCases={[
        { icon: Target, persona: "Targeting one specific role", body: "Paste the exact JD you're applying to and drill the precise questions that spec implies — no wasted practice." },
        { icon: Building2, persona: "Prepping for a known company", body: "Feed in the company's product docs or wiki so your answers speak their language, not generic theory." },
        { icon: BookOpen, persona: "Studying a dense topic", body: "Turn any long document into a focused interview and prove you actually absorbed the material." },
      ]}
      faqs={[
        { q: "What can I upload?", a: "A job description, a company wiki, a product spec, or any text you want to be interviewed on. Upload a PDF/DOCX or paste it directly." },
        { q: "How are the questions generated?", a: "The AI reads your document end-to-end, identifies the skills and responsibilities that matter, and produces a focused set of questions at the right depth for the role." },
        { q: "How is grading different from a normal mock interview?", a: "Your answers are checked against the exact material you provided — not a generic rubric. You get an answer-vs-source match score and see the specific passages you should have covered." },
        { q: "Can I regenerate the questions?", a: "Yes. If you want a fresh angle on the same document, regenerate the interview any time." },
        { q: "Is my uploaded document private?", a: "Your material is used only to generate and grade your interview. It isn't shared with other users." },
      ]}
      ctaTitle="Interview on your own terms."
      ctaSubtitle="Free to start. Upload a doc and go."
      related={siblingLinks("custom-interview")}
      seoTitle="Custom AI Interview — Turn Any JD or Doc Into an Interview | VetraHub"
      seoDescription="Upload a job description or company document and get a tailored AI interview, graded strictly against your exact material. Free to start."
    />
  );
}
